import { action, computed, observable, makeObservable } from 'mobx';
import { AmountCode } from 'typings/enums';
import { CurrencyConversion } from 'nomst/Stores/Utility/currencyConversion';
import { DecisionDispatcher } from 'nomst/Stores/Decision/decision.dispatcher';
import Coalesce from 'nomst/Stores/Utility/coalesce';
import SafeMath from 'common/safeMath';
import { IAmount } from 'typings/amount';
import * as debounce from 'javascript-debounce';

export class StockAmount {
    private readonly _amountCycleId: number;

    private readonly _employeeSourceId: string;

    private _budgetCycleId: number;

    @observable
    private  _isEligible: boolean;

    private _initialIsEligible: boolean;

    private  _isEligibleOverride: boolean;

    private _initialIsEligibleOverride: boolean;

    private _isProrated: boolean;

    private _initialIsProrated: boolean;

    @observable
    private _isProrationOverride: boolean;

    private _initialIsProrationOverride: boolean;

    private readonly _targetAmount: number;

    private readonly _amountIsoCurrencyCode: string;

    private readonly _pricePerShare: number;

    private readonly _isValueInUnit: boolean;

    private readonly _currencyConversion: CurrencyConversion;

    private readonly _homeCountryCurrency: string;

    private readonly _decisionDispatcher: DecisionDispatcher;

    @observable private _amount: number;

    @observable private _currentIsoCurrencyCode: string;

    // tslint:disable:parameters-max-number
    constructor(
        amountDto: IAmount,
        employeeSourceId: string,
        targetAmount: number,
        amountIsoCurrencyCode: string,
        currentIsoCurrencyCode: string,
        pricePerShare: number,
        isValueInUnit: boolean,
        currencyConversion: CurrencyConversion,
        homeCountryCurrency: string,
        decisionDispatcher: DecisionDispatcher) {
        makeObservable(this);
        this._amountCycleId = amountDto.amountCycleId;
        this._budgetCycleId = amountDto.budgetCycleId ?? 0;
        this._employeeSourceId = employeeSourceId;
        this._isEligible = amountDto.isEligible;
        this._initialIsEligible = amountDto.isEligible;
        this._isEligibleOverride = !!amountDto.isEligibleOverride;
        this._initialIsEligibleOverride = !!amountDto.isEligibleOverride;
        this._isProrated = amountDto.isProrated;
        this._initialIsProrated = amountDto.isProrated;
        this._isProrationOverride = !!amountDto.isProrationOverride;
        this._initialIsProrationOverride = !!amountDto.isProrationOverride;
        this._amount = amountDto.value || 0;
        this._targetAmount = targetAmount;
        this._amountIsoCurrencyCode = amountIsoCurrencyCode;
        this._currentIsoCurrencyCode = currentIsoCurrencyCode;
        this._pricePerShare = pricePerShare;
        this._isValueInUnit = isValueInUnit;
        this._currencyConversion = Coalesce.singleValue(currencyConversion, () => new CurrencyConversion(null));
        this._homeCountryCurrency = homeCountryCurrency;
        this._decisionDispatcher = decisionDispatcher;

        this.debounceAmountChanged = debounce(this.debounceAmountChanged.bind(this), 500);
    }

    @computed get amount(): number {
        return this._amount;
    }

    get targetAmount(): number {
        return this._targetAmount;
    }

    get convertedTargetAmount(): number {
        return this._currencyConversion.convertCurrency(
            this._amountIsoCurrencyCode,
            this._currentIsoCurrencyCode,
            this._targetAmount);
    }

    get amountIsoCurrencyCode(): string {
        return this._amountIsoCurrencyCode;
    }

    get isValueInUnit(): boolean {
        return this._isValueInUnit;
    }

    get currentIsoCurrencyCode(): string {
        return this._currentIsoCurrencyCode;
    }

    get amountCode(): AmountCode {
        return AmountCode.Stock;
    }

    get amountCycleId(): number {
        return this._amountCycleId;
    }

    get budgetCycleId(): number {
        return this._budgetCycleId;
    }

    get numberOfShares(): number {
        if (this._isValueInUnit) {
            return this._amount;
        }

        return SafeMath.divide(this._amount, this.pricePerShareInAmountCurrency);
    }

    get isEligible(): boolean {
        return this._isEligible;
    }

    get initialIsEligible(): boolean {
        return this._initialIsEligible;
    }

    get isEligibleOverride(): boolean {
        return this._isEligibleOverride;
    }

    get initialIsEligibleOverride(): boolean {
        return this._initialIsEligibleOverride;
    }

    get isProrated(): boolean {
        return this._isProrated;
    }

    get initialIsProrated(): boolean {
        return this._initialIsProrated;
    }

    get isProrationOverride(): boolean {
        return this._isProrationOverride;
    }

    get initialIsProrationOverride(): boolean {
        return this._initialIsProrationOverride;
    }

    get convertedMonetaryAmount(): Nullable<number> {
        if (this._isValueInUnit) {
            return null;
        }

        return this._currencyConversion.convertCurrency(
            this._amountIsoCurrencyCode,
            this._currentIsoCurrencyCode,
            this._amount);
    }

    get pricePerShareInAmountCurrency(): number {
        return this._currencyConversion.convertCurrency(
            this._homeCountryCurrency,
            this._amountIsoCurrencyCode,
            this._pricePerShare);
    }

    @action
    overrideProration(proration: boolean): void {
        this._isProrationOverride = proration;
    }

    @action
    overrideEligibility(eligibility: boolean): void {
        this._isEligible = eligibility;
    }

    @action
    setCurrentCurrency(currentIsoCurrencyCode: string): void {
        if (!currentIsoCurrencyCode) {
            this._currentIsoCurrencyCode = this._amountIsoCurrencyCode;
            return;
        }

        this._currentIsoCurrencyCode = currentIsoCurrencyCode;
    }

    @action
    setMonetaryAmount(amount: number): void {
        if (this._isValueInUnit) {
            return;
        }

        this._amount = this._currencyConversion.convertCurrency(
            this._currentIsoCurrencyCode,
            this._amountIsoCurrencyCode,
            amount);

        this.debounceAmountChanged();
    }

    @action
    resetInitialIsEligible(): void {
        this._initialIsEligible = this._isEligible;
    }

    @action
    resetInitialIsProration(): void {
        this._initialIsProrationOverride = this._isProrationOverride;
    }

    @action
    resetEligibleOverride(): void {
        this._isEligible = this._initialIsEligible;
    }

    @action
    resetProrationOverride(): void {
        this._isProrationOverride = this._initialIsProrationOverride;
    }

    @action
    setNumberOfShares(numberOfShare: number): void {
        if (this._isValueInUnit) {
            this._amount = numberOfShare;
        } else {
            this._amount = numberOfShare * this.pricePerShareInAmountCurrency;
        }

        this.debounceAmountChanged();
    }

    private debounceAmountChanged(): void {
        this._decisionDispatcher.dispatchDebounceAmountChanged(
            this._employeeSourceId,
            AmountCode.Stock,
            this._amountCycleId,
            this._amount);
    }
}